// Luftbåren forsyning: CARP (Computed Air Release Point) for fallskjerm-slipp
// over en droppsone (DZ). Regner ut HVOR flyet skal slippe lasten slik at den
// lander på DZ, gitt slipphøyde, flyets kurs/fart, vind og nedstignings-
// karakteristikk. Motsatt vei: hvor lasten faktisk lander for et gitt slippunkt.
//
// Modell (doktrine-forenklet, statisk line / rund kalesje):
//  • Flytid (ToF) = høyde over bakken / nedstigningsrate.
//  • Vindavdrift = vindfart · ToF, medvinds retning (fromDeg + 180).
//  • Framkast (forward throw) = flyets bakkefart · framkast-tidskonstant, langs kurs.
//  • CARP = DZ trukket TILBAKE langs kurs (framkast) og MOT vinden (avdrift).
// Ren + deterministisk + testbar. HALO/HAHO (fritt fall) er utenfor (egen modell).
import { project, haversineM } from './geo.js';
const KT_TO_MPS = 0.514444;
/** Representative kalesjer/laster (åpne kilder, ugradert; juster mot lokal SOP). */
export const CANOPIES = [
    { id: 'personnel', name: 'Personell (statisk line, rund)', rateOfDescentMps: 5.5, forwardThrowTimeS: 3 },
    { id: 'cargo-g12', name: 'Last G-12 (tung)', rateOfDescentMps: 7.0, forwardThrowTimeS: 2.5 },
    { id: 'cds', name: 'CDS-bunt (Container Delivery System)', rateOfDescentMps: 8.5, forwardThrowTimeS: 2 },
    { id: 'door-bundle', name: 'Dørbunt (lett)', rateOfDescentMps: 6.0, forwardThrowTimeS: 2.5 },
];
export function canopyById(id) {
    return CANOPIES.find((c) => c.id === id);
}
function resolveDescent(input) {
    const c = typeof input.canopy === 'string' ? canopyById(input.canopy) : input.canopy;
    const rate = input.rateOfDescentMps ?? c?.rateOfDescentMps ?? CANOPIES[0].rateOfDescentMps;
    const throwT = input.forwardThrowTimeS ?? c?.forwardThrowTimeS ?? CANOPIES[0].forwardThrowTimeS;
    return { rate, throwT };
}
const norm360 = (d) => ((d % 360) + 360) % 360;
/** Beregn slippunkt (CARP) slik at lasten lander på DZ. */
export function computeCarp(input) {
    const { rate, throwT } = resolveDescent(input);
    const timeOfFallS = input.dropAltitudeAglM / rate;
    const driftM = input.wind.speedKt * KT_TO_MPS * timeOfFallS;
    const driftBearingDeg = norm360(input.wind.fromDeg + 180); // medvind
    const forwardThrowM = input.aircraftSpeedKt * KT_TO_MPS * throwT;
    // CARP = DZ trukket tilbake langs kurs (framkast) og mot vinden (avdrift).
    const backAlongTrack = project(input.dz, norm360(input.aircraftHeadingDeg + 180), forwardThrowM);
    const carp = project(backAlongTrack, input.wind.fromDeg, driftM); // mot vinden
    return {
        carp,
        timeOfFallS,
        driftM,
        driftBearingDeg,
        forwardThrowM,
        distanceFromDzM: haversineM(input.dz, carp),
        bearingFromDzDeg: bearing(input.dz, carp),
    };
}
/** Motsatt vei: hvor lander lasten om den slippes ved `release`? (verifisering). */
export function impactPoint(release, input) {
    const { rate, throwT } = resolveDescent({ ...input, dz: release });
    const timeOfFallS = input.dropAltitudeAglM / rate;
    const driftM = input.wind.speedKt * KT_TO_MPS * timeOfFallS;
    const forwardThrowM = input.aircraftSpeedKt * KT_TO_MPS * throwT;
    // Framkast langs kurs, deretter medvinds avdrift.
    const afterThrow = project(release, input.aircraftHeadingDeg, forwardThrowM);
    return project(afterThrow, norm360(input.wind.fromDeg + 180), driftM);
}
/** Initial peiling (grader) fra a til b. */
function bearing(a, b) {
    const φ1 = (a.lat * Math.PI) / 180, φ2 = (b.lat * Math.PI) / 180;
    const Δλ = ((b.lon - a.lon) * Math.PI) / 180;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return norm360((Math.atan2(y, x) * 180) / Math.PI);
}
//# sourceMappingURL=airdrop.js.map